import { useState } from 'react';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import toast from '@liquidweb-dev/undercurrent/toast';
import LWForm, { FormSubmitHandler } from 'components/LWForm';
import useCloudBackupList from 'modules/queries/cloud/backup/useList';
import useServerUpdate from 'modules/queries/server/useUpdate';
import {
	ADDON_FORM_VALIDATOR,
	INITIAL_FORM_ERRORS,
	INITIAL_FORM_VALUES,
} from 'components/standard/DialogDestroyAddon/constant';
import AddonCard from 'components/standard/DialogDestroyAddon/AddonCard';
import AddonWrapper from 'components/standard/DialogDestroyAddon/AddonWrapper';
import DestroyButton from 'components/standard/DialogDestroyAddon/DestroyButton';
import DialogDestroyAddon, {
	DialogDestroyAddonProps,
} from 'components/standard/DialogDestroyAddon';
import DestoryNote from 'components/standard/DialogDestroyAddon/DestroyNote';
import ReasonList from 'components/standard/DialogDestroyAddon/ReasonList';
import useBackups from '../TotalBackupsBlock/useBackups';

export interface DialogDestroyCloudBackupProps
	extends Omit<DialogDestroyAddonProps, 'title' | 'subtitle'> {
	uniqId: string;
	domain?: string;
}

const DialogDestroyCloudBackup = ({
	uniqId,
	domain,
	onClose,
	...dialogProps
}: DialogDestroyCloudBackupProps) => {
	const { data: stormBackups } = useCloudBackupList(
		{
			uniq_id: uniqId,
			page_size: 1,
		},
		{ enabled: dialogProps.open },
	);
	const { totalSize } = useBackups(uniqId);
	const { mutate: updateServer, isLoading: destroyIsLoading } =
		useServerUpdate();

	const [formValues, setFormValues] = useState(INITIAL_FORM_VALUES);
	const [formErrors, setFormErrors] = useState(INITIAL_FORM_ERRORS);

	const backupCount = stormBackups?.item_total;
	const backupUsed = totalSize || '0 GB';

	const handleSubmit: FormSubmitHandler<typeof INITIAL_FORM_VALUES> = () => {
		updateServer(
			{
				uniq_id: uniqId,
				features: { LiquidWebBackupPlan: 'None' },
			},
			{
				onSuccess: () => {
					toast.success('Successfully cancelled Cloud Backup');
					setFormValues(INITIAL_FORM_VALUES);
					setFormErrors(INITIAL_FORM_ERRORS);
					onClose();
				},
			},
		);
	};

	const handleClose = () => {
		setFormValues(INITIAL_FORM_VALUES);
		setFormErrors(INITIAL_FORM_ERRORS);
		onClose();
	};

	return (
		<DialogDestroyAddon
			title="Canceling your service? Tell us a bit more"
			subtitle="We want to make sure your cancellation is handled quickly and correctly. Let us know why you're canceling so we can provide the proper cancellation experience."
			onClose={handleClose}
			{...dialogProps}
		>
			<LWForm
				values={formValues}
				onValuesChange={setFormValues}
				errors={formErrors}
				validators={ADDON_FORM_VALIDATOR}
				onErrorsChange={setFormErrors}
				onSubmit={handleSubmit}
			>
				<Stack gap={400}>
					<Stack gap={400}>
						<ReasonList />
						<AddonWrapper>
							<AddonCard
								title={`Cloud Backups: ${domain}`}
								info={
									<>
										<Typography
											variant="body-sm"
											sx={({ vars }) => ({
												color: vars.palette.uc.text.weak,
												fontWeight: 500,
											})}
										>
											Backup count: {backupCount}, &nbsp;
										</Typography>
										<Typography
											variant="body-sm"
											sx={({ vars }) => ({
												color: vars.palette.uc.text.weak,
											})}
										>
											Current space used: {backupUsed}
										</Typography>
									</>
								}
								infoContainerProps={{
									flexDirection: { xs: 'column', md: 'row' },
								}}
								addOnType="Cloud Backup"
							/>
						</AddonWrapper>
						<DestoryNote>
							Removal may take a few moments, or a few days for dedicated
							servers.
						</DestoryNote>
					</Stack>
					<Stack alignItems="center">
						<DestroyButton loading={destroyIsLoading} />
					</Stack>
				</Stack>
			</LWForm>
		</DialogDestroyAddon>
	);
};

export default DialogDestroyCloudBackup;
